import { useEffect, useState } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { ProgressBarWizard } from '@bambi/component-library/src/components/Wizard/ProgressBarWizard';
import { DateTime } from 'luxon';

import {
  PricingSchemeRead,
  SpaceTypeEnum,
  usePricingPricingSchemesRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';

import { SchemeMetaFormValues } from './metaDefaultValues';
import { PricingWizardSchemeConfigurationStep } from './PricingWizardSchemeConfigurationStep';
import { PricingWizardSchemeMetaStep } from './PricingWizardSchemeMetaStep';

export type PricingWizardMode = 'create' | 'edit' | 'clone';

export interface PreloadedMetadataValues {
  payerId: string;
  spaceType: SpaceTypeEnum;
  tripDate: string;
}

export type PricingWizardProps = {
  pricingSchemeId?: string;
  onCancel: () => void;
  onSuccess: () => void;
  mode: PricingWizardMode;
  preloadedMetadataValues?: PreloadedMetadataValues;
};

export function PricingWizard({
  pricingSchemeId,
  onCancel,
  onSuccess,
  mode = 'create',
  preloadedMetadataValues,
}: PricingWizardProps) {
  const [workInProgressScheme, setWorkInProgressScheme] = useState<
    SchemeMetaFormValues | undefined
  >();

  const [cloneEditData, setCloneEditData] = useState<
    PricingSchemeRead | undefined
  >();

  const { data, isLoading } = usePricingPricingSchemesRetrieveQuery(
    {
      id: pricingSchemeId || '',
    },
    {
      skip: mode === 'create' || !pricingSchemeId,
    }
  );

  const createCloneData = (
    originalData: PricingSchemeRead
  ): PricingSchemeRead => ({
    ...originalData,
    id: '',
    name: `Copy of ${originalData.name}`,
  });

  useEffect(() => {
    if (mode === 'clone' && data) {
      setCloneEditData(createCloneData(data));
    } else if (mode === 'edit') {
      setCloneEditData(data);
    }
  }, [mode, data]);

  const isDataLoaded = (): boolean => {
    return Boolean(cloneEditData?.space_types?.length);
  };

  useEffect(() => {
    if (preloadedMetadataValues) {
      const baseDate = DateTime.fromISO(preloadedMetadataValues.tripDate);
      const startDate = baseDate.minus({ months: 3 });
      const endDate = baseDate.plus({ years: 10 });
      setWorkInProgressScheme({
        name: '',
        payer_ids: [preloadedMetadataValues.payerId],
        space_types: [preloadedMetadataValues.spaceType],
        service_types: [],
        start_date: startDate.toISODate(),
        end_date: endDate.toISODate(),
        description: '',
      });
    }
  }, [preloadedMetadataValues]);

  if (isLoading || (mode !== 'create' && !isDataLoaded())) {
    return <LoadingIndicator />;
  }

  return (
    <ProgressBarWizard
      steps={[
        {
          title: 'Base Configuration',
          render: ({ next }) => (
            <PricingWizardSchemeMetaStep
              originalPricingSchemeValues={cloneEditData}
              initialValues={workInProgressScheme}
              onCancel={() => {
                setWorkInProgressScheme(undefined);
                onCancel();
              }}
              onSubmit={(values) => {
                setWorkInProgressScheme(values);
                next();
              }}
            />
          ),
        },
        {
          title: `${workInProgressScheme?.name}`,
          render: () => (
            <PricingWizardSchemeConfigurationStep
              originalPricingSchemeValues={cloneEditData}
              onCancel={() => {
                onCancel();
              }}
              onSubmit={() => {
                onSuccess();
              }}
              workInProgressScheme={
                workInProgressScheme as SchemeMetaFormValues
              }
              mode={mode}
            />
          ),
        },
      ]}
    />
  );
}
